/**
 * 단계 카드 컴포넌트 (v3)
 * - dim=true 이면 opacity + grayscale 처리 + "이 단계 건너뜀" 라벨
 * - addon=true 이면 점선 테두리로 추가 인허가 시각 구분
 * - onClick 있으면 (step.detail 존재) 클릭 가능 시그널 활성
 */
import type { Step } from './steps';

interface Props {
  step: Step;
  dim: boolean;
  addon?: boolean;
  onClick?: () => void;
}

export function StepCard({ step, dim, addon = false, onClick }: Props) {
  const clickable = !!onClick && !!step.detail;
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
      className={[
        'relative bg-surface rounded-xl p-4 flex flex-col gap-2 min-w-0 w-full',
        addon
          ? 'border border-dashed border-bdr-subtle'
          : 'border border-bdr-subtlest',
        clickable
          ? 'cursor-pointer hover:shadow-md hover:border-bdr-subtle transition-shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          : '',
      ].join(' ')}
      style={{
        opacity: dim ? 0.25 : 1,
        filter: dim ? 'grayscale(1)' : 'none',
        transition: 'opacity 300ms, filter 300ms',
      }}
    >
      {/* 단계 번호 뱃지 */}
      <div className="flex items-center justify-between">
        <span
          className={[
            'inline-flex items-center rounded-md text-[10px] font-bold px-2 py-0.5 leading-none',
            addon ? 'bg-bdr-subtle text-fg-subtle' : 'bg-fg text-surface',
          ].join(' ')}
        >
          {step.label}
        </span>
        <div className="flex items-center gap-1.5">
          {dim && (
            <span className="text-[10px] text-fg-subtler leading-none">이 단계 건너뜀</span>
          )}
          {addon && !dim && (
            <span className="text-[10px] text-fg-subtler leading-none">조건부</span>
          )}
          {clickable && !dim && (
            <span className="text-[10px] text-fg-subtler leading-none flex items-center gap-0.5">
              상세
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                <path d="M1.5 6.5l5-5M4 1.5H6.5V4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          )}
        </div>
      </div>

      {/* 단계명 */}
      <p className="text-base font-bold leading-snug">{step.name}</p>

      {/* 기관 + 소요 */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-fg-subtle">
        <span>{step.org}</span>
        <span className="text-fg-subtler">·</span>
        <span>{step.duration}</span>
      </div>

      {/* 핵심 구비서류 */}
      <ul className="text-xs text-fg-subtler leading-relaxed space-y-0.5">
        {step.docs.map((d, i) => (
          <li key={i} className="flex gap-1">
            <span className="shrink-0 text-fg-subtler">–</span>
            <span>{d}</span>
          </li>
        ))}
      </ul>

      {/* 추가 설명 */}
      {step.note && (
        <p className="text-[11px] text-fg-subtler leading-relaxed border-t border-bdr-subtlest pt-2 mt-1">
          {step.note}
        </p>
      )}
    </div>
  );
}
